import { useEffect, useRef } from "react";
import { audienceSortStepNames } from "./utility/audienceSortStepNames";
import { getLocationData } from "./utility/audienceSortApiHandlers";
import { ImageArrowLeft, ImageArrowRight, ImageX } from "components/utility/images";
import { getDecodedHtml } from "utils/getDecodedHtml";
import { setGALocation } from "./utility/setGALocation";
import { CountrySelectorMobileProps } from "./utility/audienceSortPropTypes";
import getAudienceSelectorFromLink from "components/utility/api/GetAudienceSelectorFromLink";
const CountrySelectorMobile: React.FC<CountrySelectorMobileProps> = ({
	cameFromInitialStep = false,
	currentPage,
	countryListData,
	hasCloseButton,
	setStep,
	setSplashModalAudienceTypes,
	setAudienceTypesData,
	setCameFromInitialStep,
	audienceSortTheme,
	customCloseHandler
}) => {
	const audienceSortBackButton = useRef<HTMLDivElement>(null);
	const legalTextContainer = useRef<HTMLDivElement>(null);
	const handleBackButton = (): void => {
		if (currentPage === "from-link") {
			if (cameFromInitialStep) {
				setStep(audienceSortStepNames.initialStep);
			} else {
				setStep(audienceSortStepNames.audienceTypeSelection);
			}
		} else {
			setStep(audienceSortStepNames.initialStep);
		}
	};

	const handleCountrySelection = async (countries): Promise<void> => {
		if (currentPage === "from-link") {
			const country = countries[1];

			await getAudienceSelectorFromLink(country.countryKey, countryListData.apiUrl).then(
				(apiResponse) => {
					if (apiResponse.status === "success" && typeof apiResponse.data !== "string") {
						if (setSplashModalAudienceTypes) setSplashModalAudienceTypes(apiResponse.data);
					}
				}
			);
			if (setCameFromInitialStep) setCameFromInitialStep(false);
		} else {
			await getLocationData(
				{
					isPage: false,
					source: null,
					country: countries[1]
				},
				countryListData,
				setAudienceTypesData
			);
		}
		setStep(audienceSortStepNames.audienceTypeSelection);
	};

	useEffect(() => {
		audienceSortBackButton?.current?.focus();
	}, []);

	const closeModal = (): void => {
		if (customCloseHandler) {
			customCloseHandler!();
		} else {
			setStep(audienceSortStepNames.initialStep);
		}
	};

	return (
		<div className={"country-selector-mobile " + audienceSortTheme}>
			<div className="country-selector-mobile-title">
				{currentPage !== "unknown-splash" ? (
					<>
						<div
							className="country-selector-mobile-back-button"
							role="button"
							ref={audienceSortBackButton}
							aria-label={countryListData.countrySelectionTitle}
							tabIndex={0}
							onClick={handleBackButton}
							onKeyUp={() => {}}
							data-qa="country-selector-mobile-back-button"
						>
							<ImageArrowLeft />
							<span>{countryListData.countrySelectionTitle}</span>
						</div>
						{hasCloseButton && (
							<button
								className="country-selector-mobile-close-button"
								aria-label="Close"
								onClick={closeModal}
							>
								<ImageX />
							</button>
						)}
					</>
				) : (
					<span className="country-selector-mobile-title-unknown-splash">
						{countryListData.countrySelectionTitle}
					</span>
				)}
			</div>
			<div className="country-selector-mobile-countries">
				{Object.entries(countryListData.regions).map((region, index) => {
					const countryCount = Math.floor(region[1].countries.length / 5);
					return (
						<div
							key={region[1].title}
							className={
								"country-selector-mobile-countries-column " +
								"country-selector-mobile-countries-column-" +
								(countryCount > 0 ? "auto " : "initial ") +
								(index > 0 ? "country-selector-mobile-countries-column-paddingLeft" : "")
							}
						>
							<div>
								<div className="country-selector-mobile-countries-region">{region[1].title}</div>
								<div className="country-selector-mobile-flex-countries-column-inner">
									{Object.entries(region[1].countries).map((countries) => {
										return (
											<div
												key={countries[1].title}
												className={`country-selector-mobile-countries-country ${
													countries[1].isActive ? "active-country" : ""
												}`}
												aria-label={countries[1].title}
											>
												<button
													data-a="true"
													data-a-lbl={countries[1].title}
													data-a-cat={currentPage === "nav" ? "Header" : "Audience"}
													data-a-act={
														currentPage === "nav" ? "Audience Switcher" : "Country Selector"
													}
													data-a-evt="Location Switch"
													data-a-loc={setGALocation(currentPage)}
													data-a-text={countries[1].title}
													data-a-comp="audience switcher modal"
													aria-label={countries[1].title}
													tabIndex={0}
													onClick={() =>
														countries[1].isActive
															? handleBackButton()
															: handleCountrySelection(countries)
													}
												>
													<div className="country-selector-mobile-countries-title">
														{countries[1].title}
													</div>
													{currentPage !== "unknown-splash" && !countries[1].isActive && (
														<ImageArrowRight />
													)}
												</button>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					);
				})}
			</div>
			<div
				className="country-selector-mobile-legal"
				dangerouslySetInnerHTML={{ __html: getDecodedHtml(countryListData.legalText) }}
				ref={legalTextContainer}
			/>
		</div>
	);
};

export default CountrySelectorMobile;
